// Quick-reference summaries of the core rules, used by RulesReference.
// Where the 2014 and 2024 rules differ meaningfully, both are given.

export interface BasicRule {
  category: string;
  name: string;
  description: string;
  keywords?: string[];
}

export const BASIC_RULE_CATEGORIES = [
  "Action Economy",
  "Actions",
  "Movement",
  "Combat",
  "Damage & Healing",
  "Ability Checks",
  "Spellcasting",
  "Environment",
  "Vision & Light",
  "Resting",
  "Travel",
  "Objects",
];

export const BASIC_RULES: BasicRule[] = [
  // Action Economy
  {
    category: "Action Economy",
    name: "Your Turn",
    description: "On your turn you can move up to your Speed and take one Action. You may also take one Bonus Action if something grants you one, and interact with one object for free. You can break up your movement around your Action.",
    keywords: ["turn", "round"],
  },
  {
    category: "Action Economy",
    name: "Action",
    description: "One per turn. Used for Attack, Dash, Disengage, Dodge, Help, Hide, Ready, Search, casting a spell with a casting time of 1 action, using an object, and other activities.",
  },
  {
    category: "Action Economy",
    name: "Bonus Action",
    description: "You only have a Bonus Action if a feature, spell, or other ability says you can do something as a Bonus Action. At most one per turn, and you choose when to take it during your turn. If something deprives you of your ability to take actions, you can't take Bonus Actions either.",
  },
  {
    category: "Action Economy",
    name: "Reaction",
    description: "An instant response to a trigger, which can happen on your turn or someone else's. You get one Reaction per round, and it refreshes at the start of your turn. Opportunity Attacks, Readied actions, and spells like Shield use your Reaction.",
  },
  {
    category: "Action Economy",
    name: "Free Object Interaction",
    description: "Once per turn you can interact with one object or feature of the environment for free during your move or action (draw or sheathe a weapon, open a door, pick up a dropped item, hand an item to someone). A second interaction requires your Action (Use an Object / Utilize). 2024: drawing or stowing a weapon can also be done as part of each attack made with the Attack action.",
    keywords: ["draw", "sheathe", "door", "interact"],
  },
  {
    category: "Action Economy",
    name: "Communicating",
    description: "You can speak brief utterances and gestures on your turn without using an action. Longer speeches are up to the GM.",
    keywords: ["talk", "speak"],
  },

  // Actions
  {
    category: "Actions",
    name: "Attack",
    description: "Make one melee or ranged attack (more with Extra Attack). Grapples and Shoves can replace an attack. You can move between attacks if you have more than one.",
  },
  {
    category: "Actions",
    name: "Dash",
    description: "Gain extra movement for the turn equal to your Speed (after modifiers).",
  },
  {
    category: "Actions",
    name: "Disengage",
    description: "Your movement doesn't provoke Opportunity Attacks for the rest of the turn.",
  },
  {
    category: "Actions",
    name: "Dodge",
    description: "Until the start of your next turn, attack rolls against you have Disadvantage if you can see the attacker, and you make Dexterity saving throws with Advantage. You lose the benefit if you are Incapacitated or your Speed drops to 0.",
  },
  {
    category: "Actions",
    name: "Help",
    description: "2014: Give an ally Advantage on their next ability check for a task you help with, or on their next attack roll against a creature within 5 feet of you, before the start of your next turn. 2024: either help with an ability check (you must be proficient in the skill/tool) or distract an enemy within 5 feet so the next attack roll by one of your allies against it has Advantage.",
  },
  {
    category: "Actions",
    name: "Hide",
    description: "Make a Dexterity (Stealth) check, typically while Heavily Obscured or behind Three-Quarters or Total Cover and out of an enemy's line of sight. 2024: on a DC 15 success you have the Invisible condition until you make noise louder than a whisper, attack, cast a spell with a Verbal component, or an enemy finds you. Enemies use Wisdom (Perception) against your check total to find you.",
    keywords: ["stealth", "invisible"],
  },
  {
    category: "Actions",
    name: "Ready",
    description: "Choose a perceivable trigger and an action (or up to your Speed of movement) to take in response. When the trigger occurs you can use your Reaction to do it, or ignore it. Readying a spell requires casting it now (using the slot) and holding it with Concentration until the trigger; if not released before your next turn, it's lost.",
    keywords: ["trigger"],
  },
  {
    category: "Actions",
    name: "Search",
    description: "Devote attention to finding something, usually a Wisdom (Perception) or Intelligence (Investigation) check. 2024 Search covers Insight, Medicine, Perception, and Survival checks.",
  },
  {
    category: "Actions",
    name: "Study (2024)",
    description: "Make an Intelligence check (Arcana, History, Investigation, Nature, or Religion) to recall or work out information.",
  },
  {
    category: "Actions",
    name: "Influence (2024)",
    description: "Make a Charisma (Deception, Intimidation, Performance, or Persuasion) or Wisdom (Animal Handling) check to alter a creature's attitude. Friendly creatures generally agree to reasonable requests, hostile ones won't, and indifferent ones need a successful check (default DC 15 or the monster's Intelligence score, whichever is higher).",
  },
  {
    category: "Actions",
    name: "Use an Object / Utilize",
    description: "Interact with a second object on your turn, or use an object that requires an action (drink a potion, activate some magic items, light a torch, etc.). 2024: drinking a potion is a Bonus Action.",
    keywords: ["potion", "item"],
  },
  {
    category: "Actions",
    name: "Magic / Cast a Spell",
    description: "Cast a spell with a casting time of an action, use a magic item that requires an action, or use a magical feature that takes an action.",
  },

  // Movement
  {
    category: "Movement",
    name: "Speed & Breaking Up Movement",
    description: "You can move up to your Speed each turn, and you can split it up before and after your Action (and between attacks). If you have multiple speeds (walking, flying, etc.), subtract the distance already moved from the new speed to see how far you can still go.",
    keywords: ["speed", "move"],
  },
  {
    category: "Movement",
    name: "Difficult Terrain",
    description: "Every foot of movement in Difficult Terrain costs 1 extra foot. Examples: rubble, undergrowth, steep stairs, snow, shallow bogs. A space occupied by another creature (ally or not) counts as Difficult Terrain. Extra costs from Difficult Terrain don't stack with each other.",
  },
  {
    category: "Movement",
    name: "Climbing, Swimming & Crawling",
    description: "Without a Climb or Swim Speed, each foot of climbing or swimming costs 1 extra foot (2 extra in Difficult Terrain). Crawling also costs 1 extra foot per foot. A slippery surface or one with few handholds may require a Strength (Athletics) check to climb; rough water may require a check to swim.",
    keywords: ["climb", "swim", "crawl", "prone"],
  },
  {
    category: "Movement",
    name: "Long Jump",
    description: "With a running start of at least 10 feet, you can jump a number of feet up to your Strength score. From a standing start, half that. Each foot jumped costs a foot of movement. Clearing a low obstacle (taller than a quarter of the jump distance) may require a DC 10 Strength (Athletics) check; landing in Difficult Terrain may require a DC 10 Dexterity (Acrobatics) check to avoid falling Prone.",
    keywords: ["jump"],
  },
  {
    category: "Movement",
    name: "High Jump",
    description: "With a running start of at least 10 feet, you leap 3 + your Strength modifier feet into the air (minimum 0). Standing, half that. You can extend your arms half your height above that to reach things. Each foot jumped costs a foot of movement.",
    keywords: ["jump"],
  },
  {
    category: "Movement",
    name: "Standing Up from Prone",
    description: "Standing up costs movement equal to half your Speed. You can't stand if you don't have enough movement left or your Speed is 0. Dropping Prone costs no movement.",
    keywords: ["prone"],
  },
  {
    category: "Movement",
    name: "Moving Around Other Creatures",
    description: "You can move through an ally's space, a space of a creature two or more sizes larger or smaller than you (2014: only hostile creatures 2 sizes different), or an Incapacitated creature's space (2024). Another creature's space is Difficult Terrain for you. You can't willingly end your move in another creature's space.",
    keywords: ["size", "space"],
  },
  {
    category: "Movement",
    name: "Flying Movement",
    description: "A flying creature that is knocked Prone, has its Speed reduced to 0, or is otherwise deprived of movement falls, unless it can hover or is held aloft by magic.",
    keywords: ["fly", "hover"],
  },
  {
    category: "Movement",
    name: "Squeezing",
    description: "A creature can squeeze through a space large enough for a creature one size smaller. While squeezing, every foot costs 1 extra foot, and it has Disadvantage on attack rolls and Dexterity saves, while attacks against it have Advantage.",
  },

  // Combat
  {
    category: "Combat",
    name: "Initiative",
    description: "Everyone rolls a Dexterity check at the start of combat; turns go from highest to lowest. The GM resolves ties (typically by having tied players decide among themselves and the GM decide among monsters). 2024: any Surprised creature has Disadvantage on its Initiative roll.",
  },
  {
    category: "Combat",
    name: "Surprise",
    description: "2014: The GM compares Dexterity (Stealth) checks against passive Wisdom (Perception) of the opposing side. Any creature that doesn't notice a threat is Surprised: it can't move or take an action on its first turn, and can't take a Reaction until that turn ends. 2024: Surprised creatures just roll Initiative with Disadvantage.",
    keywords: ["ambush"],
  },
  {
    category: "Combat",
    name: "Opportunity Attacks",
    description: "When a hostile creature you can see moves out of your reach, you can use your Reaction to make one melee attack against it, right before it leaves your reach. Teleporting, being moved without using its own movement, action, or reaction, or taking the Disengage action doesn't provoke.",
    keywords: ["aoo", "reaction"],
  },
  {
    category: "Combat",
    name: "Grappling",
    description: "Moving a grappled creature: your Speed is halved unless the creature is two or more sizes smaller than you. 2014: Replace one attack with a grapple against a creature no more than one size larger than you, within reach, using a free hand. Make a Strength (Athletics) check contested by the target's Strength (Athletics) or Dexterity (Acrobatics). On success the target is Grappled. 2024: Replace one attack with an Unarmed Strike; the target (no more than one size larger, within reach) makes a Strength or Dexterity save (its choice) against DC 8 + your Strength modifier + Proficiency Bonus, or is Grappled.",
    keywords: ["grapple", "grab", "athletics"],
  },
  {
    category: "Combat",
    name: "Escaping a Grapple",
    description: "The grapple also ends if the grappler is Incapacitated or an effect moves you out of its reach. 2014: Use your action for a Strength (Athletics) or Dexterity (Acrobatics) check contested by the grappler's Strength (Athletics). 2024: Use your action for a Strength (Athletics) or Dexterity (Acrobatics) check against the grapple's escape DC.",
    keywords: ["grapple", "escape"],
  },
  {
    category: "Combat",
    name: "Shoving",
    description: "The target must be no more than one size larger than you. 2014: Replace one attack with a Strength (Athletics) check contested by the target's Strength (Athletics) or Dexterity (Acrobatics); on success push it 5 feet away or knock it Prone. 2024: Replace one attack with an Unarmed Strike; the target (within reach) makes a Strength or Dexterity save against DC 8 + your Strength modifier + Proficiency Bonus or is pushed 5 feet or knocked Prone (your choice).",
    keywords: ["shove", "push", "prone"],
  },
  {
    category: "Combat",
    name: "Two-Weapon Fighting",
    description: "2014: When you take the Attack action with a Light melee weapon in one hand, you can use a Bonus Action to attack with a different Light melee weapon in the other hand. Don't add your ability modifier to the bonus attack's damage unless it's negative. 2024: Covered by the Light weapon property — the extra attack is a Bonus Action (no ability modifier to damage unless negative), or part of the Attack action if you use the Nick mastery.",
    keywords: ["dual wield", "light", "offhand"],
  },
  {
    category: "Combat",
    name: "Cover",
    description: "Half Cover (at least half the target blocked): +2 to AC and Dexterity saves. Three-Quarters Cover (about three-quarters blocked): +5 to AC and Dexterity saves. Total Cover: can't be targeted directly by attacks or spells. Only the most protective cover applies; creatures provide Half Cover.",
  },
  {
    category: "Combat",
    name: "Unseen Attackers & Targets",
    description: "Attacking a target you can't see gives you Disadvantage (and you must guess its location if it's hidden). If a target can't see you, you have Advantage on attack rolls against it. Attacking while hidden reveals your location whether you hit or miss.",
    keywords: ["invisible", "blind", "hidden"],
  },
  {
    category: "Combat",
    name: "Ranged Attacks",
    description: "Attacks beyond a weapon's normal range (out to its long range) have Disadvantage. You also have Disadvantage on ranged attack rolls when a hostile creature that can see you and isn't Incapacitated is within 5 feet of you.",
    keywords: ["range", "long range", "close combat"],
  },
  {
    category: "Combat",
    name: "Critical Hits",
    description: "A natural 20 on an attack roll always hits and is a Critical Hit: roll all of the attack's damage dice twice and add them together, then add modifiers as normal. A natural 1 always misses. 2024: weapon and Unarmed Strike crits double dice; other features specify their own effects.",
    keywords: ["crit", "nat 20", "natural 1"],
  },
  {
    category: "Combat",
    name: "Mounted Combat",
    description: "Mounting or dismounting costs half your Speed. A controlled mount moves on your initiative and can only Dash, Disengage, or Dodge. An independent mount keeps its own initiative and acts as it wishes. If the mount is moved against its will you must succeed on a DC 10 Dexterity save or fall off and land Prone within 5 feet.",
    keywords: ["mount", "horse", "ride"],
  },
  {
    category: "Combat",
    name: "Underwater Combat",
    description: "Without a Swim Speed, melee weapon attacks have Disadvantage unless using a dagger, javelin, shortsword, spear, or trident. Ranged weapon attacks automatically miss beyond normal range and have Disadvantage within normal range unless using a crossbow, net, or thrown weapon like a javelin, spear, trident, or dart. Fully submerged creatures and objects have Resistance to Fire damage.",
    keywords: ["water", "swim"],
  },
  {
    category: "Combat",
    name: "Improvised Weapons",
    description: "An object used as a weapon. If it resembles a real weapon, treat it as that weapon. Otherwise it deals 1d4 damage (GM picks the type), you don't add proficiency, and if thrown it has a range of 20/60.",
  },
  {
    category: "Combat",
    name: "Unarmed Strike",
    description: "2014: Melee attack using STR, deals 1 + STR modifier Bludgeoning damage. 2024: Choose Damage (1 + STR modifier Bludgeoning), Grapple, or Shove. You're proficient with Unarmed Strikes.",
    keywords: ["punch", "kick"],
  },

  // Damage & Healing
  {
    category: "Damage & Healing",
    name: "Resistance, Vulnerability & Immunity",
    description: "Resistance halves damage of that type (rounded down); Vulnerability doubles it; Immunity negates it. Multiple instances of resistance or vulnerability to the same damage count only once. Apply modifiers first, then resistance, then vulnerability.",
    keywords: ["resist", "immune"],
  },
  {
    category: "Damage & Healing",
    name: "Temporary Hit Points",
    description: "A buffer that absorbs damage before your real Hit Points. They don't stack: when you gain more, decide whether to keep what you have or take the new amount. Healing can't restore them, and they go away after a Long Rest unless a feature says otherwise.",
    keywords: ["temp hp", "thp"],
  },
  {
    category: "Damage & Healing",
    name: "Dropping to 0 Hit Points",
    description: "When reduced to 0 HP you fall Unconscious and begin making Death Saving Throws, unless the damage kills you outright. Monsters typically just die at 0 HP unless the GM decides otherwise.",
    keywords: ["unconscious", "down"],
  },
  {
    category: "Damage & Healing",
    name: "Instant Death",
    description: "If damage reduces you to 0 HP and there is damage remaining equal to or greater than your Hit Point maximum, you die outright.",
    keywords: ["massive damage", "death"],
  },
  {
    category: "Damage & Healing",
    name: "Death Saving Throws",
    description: "At the start of each turn at 0 HP, roll a d20 (no modifiers). 10 or higher is a success, 9 or lower a failure. Three successes: you become Stable. Three failures: you die. A natural 20 regains 1 HP. A natural 1 counts as two failures. Taking any damage while at 0 HP is one failure (a critical hit is two); damage equal to your HP maximum kills you.",
    keywords: ["death save", "dying"],
  },
  {
    category: "Damage & Healing",
    name: "Stabilizing",
    description: "Use an action to administer first aid with a DC 10 Wisdom (Medicine) check (or a Healer's Kit with no check). A Stable creature stays at 0 HP and Unconscious, but stops making death saves, and regains 1 HP after 1d4 hours. Any healing returns a creature to consciousness.",
    keywords: ["medicine", "stable"],
  },
  {
    category: "Damage & Healing",
    name: "Knocking a Creature Out",
    description: "When you reduce a creature to 0 HP with a melee attack, you can choose to knock it out instead. It falls Unconscious and is Stable.",
    keywords: ["nonlethal"],
  },

  // Ability Checks
  {
    category: "Ability Checks",
    name: "Advantage & Disadvantage",
    description: "Roll two d20s and take the higher (Advantage) or lower (Disadvantage). Multiple sources don't stack. If you have both, they cancel and you roll one d20, no matter how many of each.",
    keywords: ["adv", "dis"],
  },
  {
    category: "Ability Checks",
    name: "Typical DCs",
    description: "Very Easy 5, Easy 10, Medium 15, Hard 20, Very Hard 25, Nearly Impossible 30.",
    keywords: ["difficulty class", "dc"],
  },
  {
    category: "Ability Checks",
    name: "Contests",
    description: "Both sides make the relevant ability check; the higher total wins. On a tie, the situation stays as it was.",
    keywords: ["opposed"],
  },
  {
    category: "Ability Checks",
    name: "Passive Checks",
    description: "10 + all modifiers that normally apply to the check. Add 5 for Advantage or subtract 5 for Disadvantage. Commonly used for Perception to notice hidden threats.",
    keywords: ["passive perception"],
  },
  {
    category: "Ability Checks",
    name: "Working Together",
    description: "When one character helps another with a task they could attempt alone, the leader makes the check with Advantage (via the Help action).",
  },
  {
    category: "Ability Checks",
    name: "Group Checks",
    description: "Everyone in the group makes the check. If at least half succeed, the whole group succeeds.",
  },
  {
    category: "Ability Checks",
    name: "Proficiency Bonus",
    description: "+2 at levels 1–4, +3 at 5–8, +4 at 9–12, +5 at 13–16, +6 at 17–20. Added once to anything you're proficient in; Expertise doubles it.",
    keywords: ["pb", "expertise"],
  },

  // Spellcasting
  {
    category: "Spellcasting",
    name: "Concentration",
    description: "You can concentrate on only one spell at a time. Concentration ends if you cast another concentration spell, are Incapacitated, die, or fail a Constitution save after taking damage (DC 10 or half the damage taken, whichever is higher; 2024 caps the DC at 30). Each source of damage requires a separate save.",
    keywords: ["con save"],
  },
  {
    category: "Spellcasting",
    name: "Spell Components",
    description: "Verbal (V): spoken words; can't cast if you can't speak (e.g. Silence). Somatic (S): requires a free hand to gesture. Material (M): requires the listed materials or a component pouch/spellcasting focus in place of materials without a cost. Materials with a listed cost must actually be provided; consumed ones are used up.",
    keywords: ["verbal", "somatic", "material", "focus"],
  },
  {
    category: "Spellcasting",
    name: "Bonus Action Spells",
    description: "2014: If you cast a spell with a Bonus Action, the only other spell you can cast that turn is a cantrip with a casting time of 1 action. 2024: You can expend only one spell slot per turn.",
    keywords: ["two spells", "one spell per turn"],
  },
  {
    category: "Spellcasting",
    name: "Casting in Armor",
    description: "You must be proficient with the armor you're wearing to cast spells.",
  },
  {
    category: "Spellcasting",
    name: "Ritual Casting",
    description: "A spell with the Ritual tag can be cast without expending a slot by adding 10 minutes to its casting time. It can't be cast at a higher level this way. Requires a feature that grants ritual casting.",
  },
  {
    category: "Spellcasting",
    name: "Areas of Effect",
    description: "Cone: width at any point equals distance from origin. Cube: origin is on a face. Cylinder & Sphere: origin is the center. Line: extends from origin for its length. A point is included in the area if total cover doesn't block it from the origin.",
    keywords: ["aoe", "cone", "sphere", "line", "cube"],
  },

  // Environment
  {
    category: "Environment",
    name: "Falling",
    description: "At the end of a fall, take 1d6 Bludgeoning damage per 10 feet fallen, to a maximum of 20d6, and land Prone unless you avoid taking damage. 2024: falling into water reduces this — you can use your Reaction to make a DC 15 Strength (Athletics) or Dexterity (Acrobatics) check to take half damage. Optional rule: falling creatures drop up to 500 feet instantly, then continue at 500 feet per round.",
    keywords: ["fall", "drop", "height"],
  },
  {
    category: "Environment",
    name: "Suffocating",
    description: "You can hold your breath for 1 + Constitution modifier minutes (minimum 30 seconds). After that, or when choking, you survive for a number of rounds equal to your Constitution modifier (minimum 1); at the start of your next turn you drop to 0 HP and are dying. 2024: each turn out of breath you gain a level of Exhaustion instead.",
    keywords: ["drowning", "breath", "suffocate"],
  },
  {
    category: "Environment",
    name: "Food & Water",
    description: "A creature needs about 1 pound of food and 1 gallon of water per day (2 gallons in hot weather). It can go without food for 3 + CON modifier days (minimum 1), then gains a level of Exhaustion each day. Getting only half the needed water requires a DC 15 Constitution save each day to avoid Exhaustion.",
    keywords: ["starving", "thirst", "rations"],
  },
  {
    category: "Environment",
    name: "Extreme Cold",
    description: "At or below 0°F, a creature without cold weather gear must succeed on a DC 10 Constitution save each hour or gain a level of Exhaustion. Creatures with Cold resistance or immunity, or adapted to the cold, succeed automatically.",
    keywords: ["weather", "freezing"],
  },
  {
    category: "Environment",
    name: "Extreme Heat",
    description: "At or above 100°F without water, a creature must make a Constitution save each hour or gain a level of Exhaustion. The DC is 5 for the first hour and increases by 1 each additional hour. Medium or heavy armor or heavy clothing imposes Disadvantage. Fire resistance or immunity, or being adapted, succeeds automatically.",
    keywords: ["weather", "hot"],
  },
  {
    category: "Environment",
    name: "Strong Wind & Heavy Precipitation",
    description: "Strong wind gives Disadvantage on ranged weapon attacks and Wisdom (Perception) checks that rely on hearing, extinguishes open flames, and grounds nonmagical flying creatures. Heavy rain or snow makes the area Lightly Obscured and gives Disadvantage on Perception checks relying on sight.",
    keywords: ["weather", "rain", "snow"],
  },
  {
    category: "Environment",
    name: "Frigid Water",
    description: "A creature can be immersed in frigid water for a number of minutes equal to its Constitution score before suffering ill effects. Each additional minute requires a DC 10 Constitution save or gain a level of Exhaustion.",
    keywords: ["cold", "swim"],
  },
  {
    category: "Environment",
    name: "Thin Ice",
    description: "Thin ice has a weight tolerance of 3d10 × 10 pounds per 10-foot-square area. Whenever the total weight on an area exceeds it, the ice breaks.",
  },

  // Vision & Light
  {
    category: "Vision & Light",
    name: "Lightly Obscured",
    description: "Dim light, patchy fog, moderate foliage. Creatures have Disadvantage on Wisdom (Perception) checks that rely on sight.",
    keywords: ["dim light"],
  },
  {
    category: "Vision & Light",
    name: "Heavily Obscured",
    description: "Darkness, opaque fog, dense foliage. Vision is blocked entirely; a creature effectively has the Blinded condition when trying to see something in that area.",
    keywords: ["darkness", "fog"],
  },
  {
    category: "Vision & Light",
    name: "Bright Light, Dim Light & Darkness",
    description: "Bright light lets most creatures see normally. Dim light (shadows, twilight, the edge of a torch's radius) is Lightly Obscured. Darkness (night, unlit dungeons) is Heavily Obscured.",
    keywords: ["light", "torch"],
  },
  {
    category: "Vision & Light",
    name: "Light Sources",
    description: "Candle: 5 ft bright + 5 ft dim, 1 hour. Torch: 20 ft bright + 20 ft dim, 1 hour. Lamp: 15 ft bright + 30 ft dim, 6 hours per flask of oil. Hooded Lantern: 30 ft bright + 30 ft dim (or 5 ft dim when hooded), 6 hours per flask. Bullseye Lantern: 60 ft cone bright + 60 ft dim.",
    keywords: ["torch", "lantern", "candle"],
  },

  // Resting
  {
    category: "Resting",
    name: "Short Rest",
    description: "At least 1 hour of light activity. You can spend Hit Dice: roll each one and add your Constitution modifier to regain that many HP. 2024: a Short Rest is interrupted by combat or damage.",
    keywords: ["hit dice", "rest"],
  },
  {
    category: "Resting",
    name: "Long Rest",
    description: "At least 8 hours, with no more than 2 hours of light activity (2014: no more than 1 hour of strenuous activity). Regain all lost HP and up to half your total Hit Dice (2024: all Hit Dice), and reduce Exhaustion by 1. You can benefit from only one Long Rest per 24 hours and must have at least 1 HP to gain its benefits.",
    keywords: ["rest", "sleep"],
  },

  // Travel
  {
    category: "Travel",
    name: "Travel Pace",
    description: "Fast: 400 ft/min, 4 miles/hour, 30 miles/day; −5 to passive Perception. Normal: 300 ft/min, 3 miles/hour, 24 miles/day. Slow: 200 ft/min, 2 miles/hour, 18 miles/day; able to use Stealth.",
    keywords: ["overland", "speed", "miles"],
  },
  {
    category: "Travel",
    name: "Forced March",
    description: "Traveling more than 8 hours in a day: at the end of each additional hour, each character makes a Constitution save (DC 10 + 1 per hour past 8) or gains a level of Exhaustion.",
    keywords: ["exhaustion"],
  },
  {
    category: "Travel",
    name: "Encumbrance & Carrying Capacity",
    description: "Carrying capacity is Strength score × 15 pounds (doubled per size above Medium, halved for Tiny). You can push, drag, or lift up to twice that, but your Speed drops to 5 feet while doing so. Optional variant: over 5 × STR you're Encumbered (−10 ft Speed); over 10 × STR you're Heavily Encumbered (−20 ft Speed and Disadvantage on STR, DEX, and CON checks, attacks, and saves).",
    keywords: ["carry", "weight", "lift", "drag"],
  },

  // Objects
  {
    category: "Objects",
    name: "Object Armor Class",
    description: "Cloth, paper, rope 11. Crystal, glass, ice 13. Wood, bone 15. Stone 17. Iron, steel 19. Mithral 21. Adamantine 23.",
    keywords: ["ac", "break"],
  },
  {
    category: "Objects",
    name: "Object Hit Points",
    description: "Tiny (bottle, lock): 2 (1d4) fragile / 5 (2d4) resilient. Small (chest, lute): 3 (1d6) / 10 (3d6). Medium (barrel, chandelier): 4 (1d8) / 18 (4d8). Large (cart, 10-ft window): 5 (1d10) / 27 (5d10). Objects are immune to Poison and Psychic damage, and the GM may give them resistances or a damage threshold.",
    keywords: ["hp", "break", "door"],
  },
  {
    category: "Objects",
    name: "Breaking Objects (Strength)",
    description: "Forcing open a stuck or locked door or breaking free of bonds is a Strength (Athletics) check. Typical DCs: stuck door 10–15, locked wooden door 15–20, iron bars 20–25, manacles 20.",
    keywords: ["door", "lock", "force"],
  },
];
